import React from "react";
import { Box, Typography, styled } from "@mui/material";
import { FlashIcon } from "../icons/FlashIcon";
import { CheckCircle } from "../icons/CheckCircle";
import backpackSvgImport from "./backback.svg";
import forkRoadSvgImport from "./fork-road.svg";
import toolboxSvgImport from "./toolbox.svg";
import balanceSvgImport from "./balance.svg";
import connector1SvgImport from "./connector-1.svg";
import connector2SvgImport from "./connector-2.svg";
import connector3SvgImport from "./connector-3.svg";
import {
  PERKS_QUIRKS_XP,
  JOB_GEAR_XP,
  HIGH_SCHOOL_PRIORITIES_XP,
  AFTER_HIGH_SCHOOL_XP,
} from "../../constants";
import type { ActivityType, ActivityCompletion } from "../../types";

// SVG imports resolve as strings (Vite) or { src } objects (Next.js) depending on bundler
const toSrc = (imported: string | { src: string }): string =>
  typeof imported === "string" ? imported : imported.src;

const backpackSvg = toSrc(backpackSvgImport as string | { src: string });
const forkRoadSvg = toSrc(forkRoadSvgImport as string | { src: string });
const toolboxSvg = toSrc(toolboxSvgImport as string | { src: string });
const balanceSvg = toSrc(balanceSvgImport as string | { src: string });
const connector1Svg = toSrc(connector1SvgImport as string | { src: string });
const connector2Svg = toSrc(connector2SvgImport as string | { src: string });
const connector3Svg = toSrc(connector3SvgImport as string | { src: string });

interface ActivityPanelProps {
  activityCompletions?: Record<string, ActivityCompletion>;
  onActivityClick: (activityType: ActivityType) => void;
  hatId: string;
}

const PanelContainer = styled(Box, {
  label: "ActivityPanelContainer",
})(() => ({
  width: 368,
  flexShrink: 0,
  height: 569,
  position: "relative",
  paddingLeft: 24,
  paddingRight: 24,
}));

interface ConnectorProps {
  src: string;
  left: number;
  top: number;
  width: number;
  height: number;
  innerWidth: number;
  innerHeight: number;
}

const Connector = ({
  src,
  left,
  top,
  width,
  height,
  innerWidth,
  innerHeight,
}: ConnectorProps) => (
  <Box
    sx={{
      position: "absolute",
      left,
      top,
      width,
      height,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      pointerEvents: "none",
    }}
  >
    <Box
      sx={{
        width: innerWidth,
        height: innerHeight,
        transform: "rotate(156.23deg)",
      }}
    >
      <img
        src={src}
        alt=""
        width={innerWidth}
        height={innerHeight}
        style={{ display: "block" }}
      />
    </Box>
  </Box>
);

const ConnectorLines = () => (
  <>
    {/* High School Priorities → After High School */}
    <Connector
      src={connector3Svg}
      left={141}
      top={110.29}
      width={54.5}
      height={60.847}
      innerWidth={37.553}
      innerHeight={49.947}
    />
    {/* After High School → Job Gear */}
    <Connector
      src={connector2Svg}
      left={40}
      top={252.97}
      width={132.209}
      height={83.031}
      innerWidth={129.656}
      innerHeight={33.622}
    />
    {/* Job Gear → Perks & Quirks */}
    <Connector
      src={connector1Svg}
      left={118}
      top={340}
      width={149.224}
      height={118.34}
      innerWidth={131.64}
      innerHeight={71.33}
    />
  </>
);

const ActivityItem = styled(Box, {
  label: "ActivityItem",
})(() => ({
  position: "absolute",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
  cursor: "pointer",
  transition: "transform 0.2s ease",
  "&:hover": {
    transform: "scale(1.05)",
  },
}));

const ActivityName = styled(Typography, {
  label: "ActivityName",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 700,
  fontSize: "20px",
  lineHeight: "30px",
  color: "#ffffff",
  textAlign: "center",
  maxWidth: 157,
}));

const XPBadge = styled(Box, {
  label: "ActivityXPBadge",
})(() => ({
  display: "flex",
  alignItems: "center",
  gap: 2,
}));

const XPText = styled(Typography, {
  label: "ActivityXPText",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontSize: "16px",
  fontWeight: 600,
  lineHeight: "24px",
  color: "#77ed8b",
}));

const CompletedBadge = styled(Box, {
  label: "ActivityCompletedBadge",
})(() => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: 1,
}));

const IconContainer = styled(Box, {
  label: "ActivityIconContainer",
})(() => ({
  width: 64,
  height: 64,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
}));

interface ActivityNodeProps {
  name: string;
  xpValue: number;
  icon: React.ReactNode;
  isCompleted: boolean;
  onClick: () => void;
  style: React.CSSProperties;
}

const ActivityNode = ({
  name,
  xpValue,
  icon,
  isCompleted,
  onClick,
  style,
}: ActivityNodeProps) => (
  <ActivityItem onClick={onClick} style={style}>
    <IconContainer>
      {icon}
      {isCompleted && (
        <CompletedBadge>
          <CheckCircle width={64} height={64} />
        </CompletedBadge>
      )}
    </IconContainer>
    <ActivityName>{name}</ActivityName>
    <XPBadge>
      <FlashIcon width={16} height={16} color="#77ed8b" />
      <XPText>{xpValue} XP</XPText>
    </XPBadge>
  </ActivityItem>
);

export const ActivityPanel = ({
  activityCompletions = {},
  onActivityClick,
  hatId,
}: ActivityPanelProps) => {
  const isCompleted = (activityType: string) =>
    !!activityCompletions[`act-${activityType}-${hatId}`];

  return (
    <PanelContainer>
      <ConnectorLines />

      {/* High School Priorities - top left */}
      <ActivityNode
        name="High School Priorities"
        xpValue={HIGH_SCHOOL_PRIORITIES_XP}
        icon={
          <img
            src={backpackSvg}
            alt="High School Priorities"
            width={64}
            height={64}
          />
        }
        isCompleted={isCompleted("highschoolpriorities")}
        onClick={() => onActivityClick("highschoolpriorities")}
        style={{ left: 16, top: 22, width: 140 }}
      />

      {/* After High School - center right */}
      <ActivityNode
        name="After High School"
        xpValue={AFTER_HIGH_SCHOOL_XP}
        icon={
          <img
            src={forkRoadSvg}
            alt="After High School"
            width={64}
            height={64}
          />
        }
        isCompleted={isCompleted("afterhighschool")}
        onClick={() => onActivityClick("afterhighschool")}
        style={{ left: 140, top: 154, width: 160 }}
      />

      {/* Job Gear - bottom left */}
      <ActivityNode
        name="Job Gear"
        xpValue={JOB_GEAR_XP}
        icon={<img src={toolboxSvg} alt="Job Gear" width={64} height={64} />}
        isCompleted={isCompleted("jobgear")}
        onClick={() => onActivityClick("jobgear")}
        style={{ left: 0, top: 347, width: 160 }}
      />

      {/* Perks & Quirks - bottom right */}
      <ActivityNode
        name={"Perks & Quirks"}
        xpValue={PERKS_QUIRKS_XP}
        icon={
          <img src={balanceSvg} alt="Perks & Quirks" width={64} height={64} />
        }
        isCompleted={isCompleted("perksquirks")}
        onClick={() => onActivityClick("perksquirks")}
        style={{ left: 222, top: 355, width: 160 }}
      />
    </PanelContainer>
  );
};
